import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LabelIcon from "@mui/icons-material/Label";
import ScienceIcon from "@mui/icons-material/Science";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import * as DefaultRoutes from "@Routes/DefaultRoutes";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.shadows[3],
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "48px 1fr",
  gap: theme.spacing(1),
  alignItems: "center",
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: theme.palette.action.hover,
}));

const IconWrapper = styled(Box)(({ theme, color = "primary" }) => {
  const main = theme.palette[color]?.main || theme.palette.primary.main;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: alpha(main, 0.12),
    color: main,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  };
});

const ExtensionRequestDetails = () => {
  const navigate = useNavigate();
  const { extensionRequestId } = useParams();
  const location = useLocation();
  const requestData = location.state?.requestData;

  const handleBack = () => navigate(-1);

  if (!requestData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Extension Request Details
        </Typography>
        <Typography sx={{ mb: 2 }}>
          No request data available for extension request {extensionRequestId}.
        </Typography>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBackIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "90vh",
        padding: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexShrink: 0 }}>
        <Tooltip title="Go Back">
          <IconButton
            onClick={handleBack}
            sx={(theme) => ({
              mr: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: theme.palette.primary.main,
                color: theme.palette.common.white,
              },
            })}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography
          variant="h5"
          sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.main })}
        >
          Extension Request Details
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
        <Grid item xs={12} sx={{ height: "100%", display: "flex" }}>
          <StyledCard
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={(theme) => ({
                p: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                color: theme.palette.common.white,
              })}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {requestData.exitRequestIngredientName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                CAS: {requestData.exitRequestIngredientCasNumber || "-"}
              </Typography>
            </Box>

            <CardContent sx={{ flex: 1, overflow: "auto", p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow>
                    <IconWrapper color="primary">
                      <AccountCircleIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Requested By
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.requesterUsername || "-"}
                      </Typography>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <IconWrapper color="success">
                      <ScienceIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.categoryName || "-"}
                      </Typography>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <IconWrapper color="warning">
                      <LabelIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phase
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.phase === "IA" ? "Impact Assessment" : "Exit Plan"}
                      </Typography>
                    </Box>
                  </InfoRow>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InfoRow>
                    <IconWrapper color="info">
                      <EventNoteIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Original Deadline
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.baseEscalationDate
                          ? dayjs(requestData.baseEscalationDate).format("MMM DD, YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <IconWrapper color="secondary">
                      <HistoryIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Requested Deadline
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.extendedEscalationDate
                          ? dayjs(requestData.extendedEscalationDate).format("MMM DD, YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                  </InfoRow>
                  <InfoRow>
                    <IconWrapper color="error">
                      <HistoryIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Effective Deadline
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {requestData.effectiveEscalationDate
                          ? dayjs(requestData.effectiveEscalationDate).format("MMM DD, YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                  </InfoRow>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Chip label={(requestData.status || "-").replace(/_/g, " ")} color="primary" />
                <Chip label={`${requestData.requestedWeeks || 0} week(s)`} color="secondary" />
              </Box>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Reason
              </Typography>
              <Box
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.default,
                  whiteSpace: "pre-wrap",
                  minHeight: 120,
                })}
              >
                {requestData.reason || "No reason provided."}
              </Box>

            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExtensionRequestDetails;
